/**
 * R6 Bio-Geometric Tab — UI for the research-side R6 engine (v1.1)
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Three sections:
 *   1. Image Analysis — upload a pathology/research image, get fractal
 *      dimension + cell-centroid detection + Fibonacci packing + composite
 *      Geometric Health Score. Image is decoded client-side via Canvas,
 *      downsampled to 256×256, and sent as a grayscale matrix to the
 *      backend (no server-side image library needed).
 *   2. Cymatic Simulator — frequency + plate dimensions → eigenmode amplitude
 *      heatmap + Chladni nodal pattern. EVIDENCE LEVEL 3 — output is
 *      computational hypothesis only and the disclaimer is surfaced in the
 *      UI prominently.
 *   3. Run History — recent R6 audit-log entries from research_engine_runs.
 *
 * Every result surfaces its evidence_level prominently. The cymatic section
 * has a bold warning banner because L3 outputs are the highest-risk surface
 * for downstream misinterpretation.
 *
 * Plain CSS only per CLAUDE.md convention.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ImageIcon,
  Waves,
  History as HistoryIcon,
  AlertTriangle,
  Upload,
  CheckCircle,
  Loader,
} from 'lucide-react';

// ── IPC wrapper that works on Electron, web, and Capacitor ──────────────────
async function ipc(channel, ...args) {
  if (typeof window !== 'undefined' && window.zignagenixAPI?.invoke) {
    return window.zignagenixAPI.invoke(channel, ...args);
  }
  // Web fallback — POST to /api/ipc
  const res = await fetch('/api/ipc', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel, args }),
  });
  if (!res.ok) throw new Error(`IPC ${channel} HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.message || `IPC ${channel} error`);
  return data;
}

// ── Image → grayscale matrix (client-side decode + downsample) ─────────────
async function decodeImageToGrayscale(file, targetSize = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to decode image (unsupported format?)'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetSize;
        canvas.height = targetSize;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, targetSize, targetSize);
        const pixels = ctx.getImageData(0, 0, targetSize, targetSize).data;
        const matrix = [];
        for (let y = 0; y < targetSize; y++) {
          const row = new Array(targetSize);
          for (let x = 0; x < targetSize; x++) {
            const i = (y * targetSize + x) * 4;
            // ITU-R BT.601 luminance
            const lum = (0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]) / 255;
            row[x] = lum;
          }
          matrix.push(row);
        }
        resolve({ matrix, originalSize: [img.naturalWidth, img.naturalHeight] });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ── Diverging heatmap render for the cymatic amplitude grid ────────────────
function renderHeatmap(canvas, grid) {
  if (!canvas || !Array.isArray(grid) || grid.length === 0) return;
  const ctx = canvas.getContext('2d');
  const H = grid.length;
  const W = grid[0].length;
  canvas.width = W;
  canvas.height = H;
  const imageData = ctx.createImageData(W, H);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const v = Math.max(-1, Math.min(1, grid[y][x] || 0));
      // Diverging: blue (-1) → white (0) → red (+1)
      let r, g, b;
      if (v >= 0) {
        r = 255;
        g = Math.round(255 * (1 - v));
        b = Math.round(255 * (1 - v));
      } else {
        r = Math.round(255 * (1 + v));
        g = Math.round(255 * (1 + v));
        b = 255;
      }
      const i = (y * W + x) * 4;
      imageData.data[i] = r;
      imageData.data[i + 1] = g;
      imageData.data[i + 2] = b;
      imageData.data[i + 3] = 255;
    }
  }
  ctx.putImageData(imageData, 0, 0);
}

// ── Evidence-level badge component ─────────────────────────────────────────
function EvidenceBadge({ level }) {
  if (!level) return null;
  const isL3 = /Level 3/i.test(level);
  const isL1 = /Level 1/i.test(level);
  const bg = isL3 ? '#fff3cd' : isL1 ? '#d4edda' : '#d1ecf1';
  const border = isL3 ? '#856404' : isL1 ? '#155724' : '#0c5460';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        background: bg,
        border: `1px solid ${border}`,
        color: border,
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 600,
        marginLeft: 8,
      }}
    >
      {level}
    </span>
  );
}

// ── Image-type options (must match backend IMAGE_TYPE_CALIBRATIONS keys) ──
// Radiology is intentionally non-selectable — visible to surface the roadmap
// to researchers ("yes, R6 plans to support this eventually") but not
// clickable so it can't be misapplied. The backend scope-guard still rejects
// it as a defense-in-depth net for any caller that bypasses this UI (e.g.,
// direct IPC use).
const IMAGE_TYPE_OPTIONS = [
  { key: 'histology_he',          label: 'Histology — H&E stained (dark cells on light tissue)', disabled: false },
  { key: 'histology_ihc',         label: 'Histology — IHC stained (DAB on counterstain)',         disabled: false },
  { key: 'cytology_fluorescence', label: 'Cytology — Fluorescence (DAPI / GFP / confocal)',      disabled: false },
  { key: 'radiology',             label: 'Radiology — X-ray / CT / MRI  —  coming in R6 v2',     disabled: true  },
  { key: 'other',                 label: 'Other / unknown (no per-type calibration applied)',    disabled: false },
];

// ── Image-analysis sub-section ─────────────────────────────────────────────
function AnalyzeImageSection() {
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageType, setImageType] = useState('histology_he');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const onFileChange = useCallback((e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError(`Unsupported file type: ${f.type}. Use JPEG or PNG.`);
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setError(`File too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Max 20 MB.`);
      return;
    }
    setError(null);
    setResult(null);
    setImageFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  }, []);

  const onAnalyze = useCallback(async () => {
    if (!imageFile) return;
    setAnalyzing(true);
    setError(null);
    setResult(null);
    try {
      const { matrix, originalSize } = await decodeImageToGrayscale(imageFile, 256);
      const response = await ipc('research:r6:analyze-image', {
        grayscale_matrix: matrix,
        threshold: 0.5,
        detect_cells: true,
        image_id: imageFile.name,
        image_type: imageType,
      });
      if (response.error) throw new Error(response.message);
      setResult({ ...response, _originalSize: originalSize });
    } catch (err) {
      setError(err.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }, [imageFile]);

  const radioWarning = imageType === 'radiology';
  const otherWarning = imageType === 'other';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* Upload zone */}
        <div style={{ flex: 1, minWidth: 280 }}>
          {/* Image type selector — v1.2 */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>
              Image type (determines calibration)
            </label>
            <select
              value={imageType}
              onChange={(e) => setImageType(e.target.value)}
              style={{
                width: '100%', padding: 8, border: '1px solid #ccc',
                borderRadius: 4, fontSize: 13, background: '#fff',
              }}
            >
              {IMAGE_TYPE_OPTIONS.map((opt) => (
                <option
                  key={opt.key}
                  value={opt.key}
                  disabled={opt.disabled}
                  style={opt.disabled ? { color: '#aaa', fontStyle: 'italic' } : undefined}
                >
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Pre-submit scope warning for unsupported types */}
          {(radioWarning || otherWarning) && (
            <div style={{
              padding: 10, marginBottom: 12, background: '#fff3cd',
              border: '1px solid #856404', color: '#856404', borderRadius: 4, fontSize: 12,
            }}>
              <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {radioWarning
                ? 'Radiology images are not yet calibrated in R6 v1.2. Submitting will return a scope-warning result without computing potentially misleading numbers.'
                : 'No image-type-specific calibration will be applied. Results may not be meaningful — select a specific image type when possible.'}
            </div>
          )}

          <label
            htmlFor="r6-file-input"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, padding: 24, border: '2px dashed #aabbcc',
              borderRadius: 8, cursor: 'pointer', background: '#f8f9fa',
              minHeight: 120, textAlign: 'center',
            }}
          >
            <Upload size={20} />
            <span>{imageFile ? imageFile.name : 'Click to upload cellular pathology image (JPEG or PNG)'}</span>
          </label>
          <input
            id="r6-file-input"
            type="file"
            accept="image/jpeg,image/png"
            onChange={onFileChange}
            style={{ display: 'none' }}
          />
          {previewUrl && (
            <img
              src={previewUrl}
              alt="upload preview"
              style={{
                marginTop: 12, maxWidth: '100%', maxHeight: 200,
                border: '1px solid #ccc', borderRadius: 4,
              }}
            />
          )}
          <button
            onClick={onAnalyze}
            disabled={!imageFile || analyzing}
            style={{
              marginTop: 12, padding: '10px 20px', border: 'none', borderRadius: 6,
              background: !imageFile || analyzing ? '#bbb' : '#2E8B98',
              color: '#fff', cursor: !imageFile || analyzing ? 'not-allowed' : 'pointer',
              fontSize: 14, fontWeight: 600,
            }}
          >
            {analyzing ? <><Loader size={14} style={{ marginRight: 6 }} /> Analyzing…</>
                       : 'Analyze image'}
          </button>
        </div>

        {/* Results */}
        <div style={{ flex: 1.5, minWidth: 360 }}>
          {error && (
            <div style={{
              padding: 12, background: '#f8d7da', border: '1px solid #f5c6cb',
              color: '#721c24', borderRadius: 4,
            }}>
              <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {error}
            </div>
          )}
          {result && result.scope_warning && (
            <div style={{
              padding: 16, background: '#fff3cd', border: '2px solid #856404',
              color: '#856404', borderRadius: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <AlertTriangle size={18} />
                <strong style={{ fontSize: 15 }}>Scope warning — analysis not computed</strong>
              </div>
              <div style={{ fontSize: 13, marginBottom: 6 }}>
                <strong>{result.calibration && result.calibration.label}</strong>
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                {result.calibration && result.calibration.scope_note}
              </div>
              <div style={{ fontSize: 11, marginTop: 8, color: '#666', fontStyle: 'italic' }}>
                R6 deliberately did NOT compute fractal/centroid/Fibonacci values for
                this image type because the current calibration would produce
                misleading labels. {result.calibration && result.calibration.calibration_source}
              </div>
            </div>
          )}
          {result && !result.scope_warning && result.health_score && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Calibration source banner */}
              {result.calibration && (
                <div style={{
                  padding: 8, background: '#e9f5f7', border: '1px solid #2E8B98',
                  borderRadius: 4, fontSize: 11, color: '#256F7A',
                }}>
                  <strong>Calibration:</strong> {result.calibration.label || '—'}
                  {Array.isArray(result.calibration.healthy_range) && (
                    <>
                      {' '}・ healthy range [{result.calibration.healthy_range.join('–')}]
                    </>
                  )}
                  {result.calibration.disrupted_threshold != null && (
                    <> ・ disrupted ≥ {result.calibration.disrupted_threshold}</>
                  )}
                </div>
              )}
              {/* Composite Health Score — headline */}
              <div style={{
                padding: 16, background: '#f0f8ff', border: '2px solid #2E8B98',
                borderRadius: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 14, color: '#555' }}>Geometric Health Score</span>
                  <EvidenceBadge level={result.overall_evidence_level} />
                </div>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#2E8B98' }}>
                  {result.health_score?.score ?? '—'} / 100
                </div>
                <div style={{
                  fontSize: 16, fontWeight: 600,
                  color: result.health_score?.label === 'healthy' ? '#1F8A4C' :
                         result.health_score?.label === 'borderline' ? '#B8740F' : '#9A1F2F',
                }}>
                  {(result.health_score?.label || 'unknown').toUpperCase()}
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                  {result.health_score?.interpretation || ''}
                </div>
              </div>

              {/* Fractal dimension */}
              {result.fractal && (
                <div style={{ padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600 }}>Fractal dimension (box-counting)</span>
                    <EvidenceBadge level={result.fractal.evidence_level} />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#2E8B98' }}>
                    {Number.isFinite(result.fractal.dimension) ? result.fractal.dimension.toFixed(4) : 'N/A'}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    R² = {Number.isFinite(result.fractal.r_squared) ? result.fractal.r_squared.toFixed(3) : 'N/A'}
                    {' '}・ {result.fractal.clinical_interpretation || ''}
                  </div>
                </div>
              )}

              {/* Cell centroids + Fibonacci */}
              {result.cell_centroids && (
                <div style={{ padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600 }}>Cell centroids + Fibonacci packing</span>
                    <EvidenceBadge level={result.cell_centroids.evidence_level} />
                  </div>
                  <div style={{ fontSize: 12, marginTop: 6 }}>
                    Detected {result.cell_centroids.count ?? 0} components
                    {result.cell_centroids.truncated && ' (truncated)'}.
                  </div>
                  {result.fibonacci && Number.isFinite(result.fibonacci.score) && (
                    <div style={{ fontSize: 14, marginTop: 4 }}>
                      Fibonacci packing score: <strong>{result.fibonacci.score.toFixed(3)}</strong>
                      {Number.isFinite(result.fibonacci.empirical_ratio) && (
                        <>
                          {' '}(empirical NN ratio: {result.fibonacci.empirical_ratio.toFixed(3)},
                          {' '}ideal: {(result.fibonacci.ideal_ratio ?? (1 / 1.618)).toFixed(3)})
                        </>
                      )}
                    </div>
                  )}
                  {!result.fibonacci && (
                    <div style={{ fontSize: 12, color: '#666' }}>
                      Need ≥ 3 detected components for Fibonacci scoring.
                    </div>
                  )}
                </div>
              )}

              <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                {result.note || ''}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Cymatic Simulator sub-section ──────────────────────────────────────────
function CymaticSimulatorSection() {
  const [params, setParams] = useState({
    frequency_hz: 5000,
    width_mm: 100,
    height_mm: 100,
    wave_speed_mps: 1540,
  });
  const [simulating, setSimulating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const ampCanvasRef = useRef(null);
  const nodalCanvasRef = useRef(null);

  const onChange = (k) => (e) => {
    const v = parseFloat(e.target.value);
    setParams((p) => ({ ...p, [k]: Number.isFinite(v) ? v : 0 }));
  };

  const onSimulate = async () => {
    setSimulating(true);
    setError(null);
    setResult(null);
    try {
      const response = await ipc('research:r6:simulate-cymatics', params);
      if (response.error) throw new Error(response.message);
      setResult(response);
    } catch (err) {
      setError(err.message || 'Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  useEffect(() => {
    if (result && ampCanvasRef.current) renderHeatmap(ampCanvasRef.current, result.amplitude_grid);
    if (result && nodalCanvasRef.current) renderHeatmap(nodalCanvasRef.current, result.nodal_grid);
  }, [result]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* L3 Warning Banner — prominent */}
      <div style={{
        padding: 16, background: '#fff3cd', border: '2px solid #856404',
        borderRadius: 4, color: '#856404',
      }}>
        <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
        <strong>Evidence Level 3 — computational hypothesis only.</strong> Cymatic
        outputs in this section are produced by a numerical solver for the 2D
        Helmholtz equation on a rectangular membrane. They are NOT validated
        for clinical use. Any treatment-relevant interpretation requires
        preclinical validation before being acted upon.
      </div>

      <div style={{ display: 'flex', gap: 20 }}>
        {/* Input form */}
        <div style={{ flex: 1, minWidth: 260 }}>
          {[
            { k: 'frequency_hz',    label: 'Frequency (Hz)' },
            { k: 'width_mm',        label: 'Plate width (mm)' },
            { k: 'height_mm',       label: 'Plate height (mm)' },
            { k: 'wave_speed_mps',  label: 'Wave speed (m/s, default 1540 for soft tissue)' },
          ].map(({ k, label }) => (
            <div key={k} style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                {label}
              </label>
              <input
                type="number"
                value={params[k]}
                onChange={onChange(k)}
                style={{
                  width: '100%', padding: 8, border: '1px solid #ccc',
                  borderRadius: 4, fontSize: 14,
                }}
              />
            </div>
          ))}
          <button
            onClick={onSimulate}
            disabled={simulating}
            style={{
              padding: '10px 20px', border: 'none', borderRadius: 6,
              background: simulating ? '#bbb' : '#2E8B98',
              color: '#fff', cursor: simulating ? 'not-allowed' : 'pointer',
              fontSize: 14, fontWeight: 600,
            }}
          >
            {simulating ? 'Simulating…' : 'Simulate cymatic pattern'}
          </button>
        </div>

        {/* Visualization */}
        <div style={{ flex: 1.5, minWidth: 360 }}>
          {error && (
            <div style={{
              padding: 12, background: '#f8d7da', border: '1px solid #f5c6cb',
              color: '#721c24', borderRadius: 4,
            }}>
              <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {error}
            </div>
          )}
          {result && result.matched_mode && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>Matched eigenmode</span>
                <EvidenceBadge level={result.evidence_level} />
              </div>
              <div style={{ fontSize: 13 }}>
                (m, n) = ({result.matched_mode.m ?? '—'}, {result.matched_mode.n ?? '—'}){' '}
                ・ f<sub>m,n</sub> = {Number.isFinite(result.matched_mode.f_mn) ? result.matched_mode.f_mn.toFixed(1) : 'N/A'} Hz{' '}
                ・ residual: {Number.isFinite(result.parameters?.eigenmode_residual_hz)
                  ? result.parameters.eigenmode_residual_hz.toFixed(1) : 'N/A'} Hz
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    Amplitude field
                  </div>
                  <canvas
                    ref={ampCanvasRef}
                    style={{
                      border: '1px solid #ccc', borderRadius: 4,
                      width: 192, height: 192, imageRendering: 'pixelated',
                    }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    Chladni nodal pattern
                  </div>
                  <canvas
                    ref={nodalCanvasRef}
                    style={{
                      border: '1px solid #ccc', borderRadius: 4,
                      width: 192, height: 192, imageRendering: 'pixelated',
                    }}
                  />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                {result.note}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Run History sub-section ────────────────────────────────────────────────
function RunHistorySection() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ipc('research:r6:runs:list', { limit: 50 });
      setRuns(response.runs || []);
    } catch (err) {
      setError(err.message || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Recent R6 runs (audit log)</h3>
        <button
          onClick={load}
          disabled={loading}
          style={{
            padding: '6px 12px', border: '1px solid #2E8B98', borderRadius: 4,
            background: '#fff', color: '#2E8B98',
            cursor: loading ? 'not-allowed' : 'pointer', fontSize: 12,
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {error && <div style={{ color: '#721c24' }}>{error}</div>}
      <div className="responsive-scroll" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>When</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Function</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Evidence</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Status</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Duration (ms)</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && !loading && (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#888' }}>
                No runs yet.
              </td></tr>
            )}
            {runs.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: 8 }}>{new Date(r.created_at).toLocaleString()}</td>
                <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 12 }}>{r.function_name}</td>
                <td style={{ padding: 8 }}>
                  <EvidenceBadge level={r.evidence_level} />
                </td>
                <td style={{ padding: 8 }}>
                  {r.status === 'ok' ? (
                    <span style={{ color: '#1F8A4C' }}><CheckCircle size={12} style={{ verticalAlign: 'middle' }} /> ok</span>
                  ) : (
                    <span style={{ color: '#9A1F2F' }} title={r.error_message}>
                      <AlertTriangle size={12} style={{ verticalAlign: 'middle' }} /> error
                    </span>
                  )}
                </td>
                <td style={{ padding: 8, textAlign: 'right' }}>{r.duration_ms || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function R6BiogeoTab() {
  const [section, setSection] = useState('analyze');

  const SECTIONS = [
    { key: 'analyze', label: 'Image Analysis', Icon: ImageIcon },
    { key: 'cymatic', label: 'Cymatic Simulator', Icon: Waves },
    { key: 'history', label: 'Run History',      Icon: HistoryIcon },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: '#2E8B98' }}>
          R6 — Bio-Geometric, Fractal & Cymatic Protocol Engine
        </h2>
        <p style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
          Research-side intelligence engine. All outputs carry an evidence-level
          tag (L1 clinical / L2 prior / L3 hypothesis). Internal research use only;
          treatment-relevant interpretation requires physician review.
        </p>
      </div>

      {/* Section nav */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 16,
        borderBottom: '1px solid #e2e8f0', paddingBottom: 0,
      }}>
        {SECTIONS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setSection(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 16px', border: 'none', background: 'none',
              color: section === key ? '#2E8B98' : '#666',
              fontWeight: section === key ? 600 : 400,
              borderBottom: section === key ? '3px solid #2E8B98' : '3px solid transparent',
              cursor: 'pointer', fontSize: 14,
            }}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {section === 'analyze' && <AnalyzeImageSection />}
      {section === 'cymatic' && <CymaticSimulatorSection />}
      {section === 'history' && <RunHistorySection />}
    </div>
  );
}
